import { useEffect, useState, useCallback, useRef } from "react";
import { PageNav } from "../components/PageNav";
import ApiCall from "../auth/ApiCall";
import { useAdaptivePolling } from "../hooks/useAdaptivePolling";
import GamesComponent from "../components/Games";
import ErrorMessage from "../components/ErrorMessage";
import "../index.css";
import "./Games.css";
import Footer from "../components/Footer";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  cacheProfileSetting,
  AccessVerification,
  RuleLimitCheck,
} from "../auth/UserProfile";

interface PickerOption {
  label: string;
  value: string;
}

interface CustomPickerProps {
  options: PickerOption[];
  selectedValue: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  prefix_text: string;
  visible: boolean;
  onClose: () => void;
}

const CustomPicker: React.FC<CustomPickerProps> = ({
  options,
  selectedValue,
  onValueChange,
  placeholder,
  prefix_text = "",
  visible,
  onClose,
}) => {
  const handleSelect = (value: string) => {
    onValueChange(value);
    onClose();
  };

  if (!visible) {
    return (
      <p
        className="font_family_base picker-text"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        <span>
          {prefix_text}
          {selectedValue || placeholder}
        </span>
        <span
          style={{
            display: "inline-block",
            width: 6,
            height: 6,
            borderRight: "2px solid #fff",
            borderBottom: "2px solid #fff",
            transform: "rotate(45deg)",
            marginBottom: 3,
            flexShrink: 0,
          }}
        />
      </p>
    );
  }

  return (
    <>
      <p className="picker-text">
        {prefix_text}
        {selectedValue || placeholder}
      </p>
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-list">
            {options.map((item) => (
              <div
                key={item.value}
                className={`font_family_base modal-item ${
                  item.label === selectedValue || item.value === selectedValue
                    ? "modal-item-selected"
                    : ""
                }`}
                onClick={() => handleSelect(item.value)}
              >
                {item.label}
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default function Games() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isViewingCurrentGames, setIsViewingCurrentGames] = useState(true);
  const [showAlertsOnly, setShowAlertsOnly] = useState(false);
  const [showMyGamesOnly, setShowMyGamesOnly] = useState(false);
  const [activeGames, setActiveGames] = useState<{ [key: string]: unknown }>(
    {},
  );
  const [isActiveGamesLoading, setIsActiveGamesLoading] = useState(true);
  const [activeGamesError, setActiveGamesError] = useState<string | null>(null);
  // const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  // const [refreshing, setRefreshing] = useState(false);
  const [selectedSport, setSelectedSport] = useState("0");
  const [selectedBook, setSelectedBook] = useState("DraftKings");
  const [selectedSportName, setSelectedSportName] = useState("ALL");
  const [sportModalVisible, setSportModalVisible] = useState(false);
  const [bookModalVisible, setBookModalVisible] = useState(false);
  const [sports, setSports] = useState([{ label: "ALL", value: "0" }]);
  const [books, setBooks] = useState([
    { label: "DraftKings", value: "DraftKings" },
  ]);
  // const [bookMap, setBookMap] = useState<Record<string, number>>({});

  const updateSelectedSport = (value: string) => {
    for (const sport in sports) {
      if (sports[sport].value === value) {
        setSelectedSportName(sports[sport].label);
      }
    }
    setSportModalVisible(false);
    setSelectedSport(value);
  };

  const updateSelectedBook = (value: string) => {
    setBookModalVisible(false);
    setSelectedBook(value);
  };

  const lastSuccessRef = useRef<number | null>(null);

  const fetchData = useCallback(async (signal: AbortSignal) => {
    try {
      const response = await ApiCall.makeAuthenticatedRequest(
        "/api/active_games_data_lite/",
        { signal },
      );

      if (signal.aborted) return;
      if ("error" in response) {
        throw new Error("Network response was not ok");
      } else {
        const sportList = [];
        const sportIdMap: Record<string, string> = { ALL: "0" };
        const responseData = response as Record<string, unknown>;
        for (const sport in responseData["sportsMetadata"] as Record<
          string,
          unknown
        >) {
          const sport_name = (
            (responseData["sportsMetadata"] as Record<string, unknown>)[
              sport
            ] as Record<string, unknown>
          )["name"] as string;
          sportList.push(sport_name);
          sportIdMap[sport_name] = sport;
        }

        const sortedSportList = ["ALL", ...sportList.sort()];
        const cleanSportList = sortedSportList.map((name) => ({
          label: name,
          value: sportIdMap[name],
        }));
        setSports((prev) => {
          const same =
            prev.length === cleanSportList.length &&
            prev.every((s, i) => s.value === cleanSportList[i].value);
          return same ? prev : cleanSportList;
        });

        const bookList: PickerOption[] = [];
        for (const book in responseData["books"] as Record<string, unknown>) {
          const book_data = (responseData["books"] as Record<string, unknown>)[
            book
          ] as Record<string, unknown>;
          if (book_data["main_odds"]) {
            bookList.push({
              label: book_data["name"] as string,
              value: book_data["name"] as string,
            });
          }
        }
        setBooks((prev) => {
          const same =
            prev.length === bookList.length &&
            prev.every((b, i) => b.value === bookList[i].value);
          return same ? prev : bookList;
        });

        setActiveGames(response);
      }
      // setLastRefreshed(new Date());
      lastSuccessRef.current = Date.now();
      setActiveGamesError(null);
    } catch (err: unknown) {
      const neverLoaded = lastSuccessRef.current === null;
      const stale =
        lastSuccessRef.current !== null &&
        Date.now() - lastSuccessRef.current >= 60_000;
      if (neverLoaded || stale) {
        setActiveGamesError(
          "Failed to fetch data: " +
            (err instanceof Error ? err.message : "Unknown error"),
        );
      }
      // console.error("Fetch error:", err);
    } finally {
      if (!signal.aborted) {
        setIsActiveGamesLoading(false);
      }
      // setRefreshing(false);
    }
  }, []);

  // Check if user just returned from Stripe checkout and refresh profile
  useEffect(() => {
    const checkSubscriptionUpdate = async () => {
      // Check if coming from Stripe checkout or if user just subscribed
      const sessionId = searchParams.get("session_id");
      const newUser = localStorage.getItem("new_user");

      if (sessionId || newUser === "true") {
        //User just completed subscription, refresh their profile data
        await cacheProfileSetting(navigate);
      }

      // Clear the new_user flag if it was set
      if (newUser === "true") {
        localStorage.setItem("new_user", "false");
      }

      // Remove session_id from URL if present
      if (sessionId) {
        searchParams.delete("session_id");
        navigate({ search: searchParams.toString() }, { replace: true });
      }

      // Verify user has an active subscription
      const redirectPath = AccessVerification() || RuleLimitCheck();
      if (redirectPath) {
        navigate(redirectPath);
        return;
      }
    };

    checkSubscriptionUpdate();
  }, [searchParams, navigate]);

  // Initial load + auto-refresh (5s, backs off to 15s on a slow connection)
  useAdaptivePolling(fetchData);

  const UpdateViewingCurrentGames = () => {
    setIsViewingCurrentGames(!isViewingCurrentGames);
  };

  const UpdateAlertsOnly = () => {
    setShowAlertsOnly(!showAlertsOnly);
  };

  const UpdateMyGamesOnly = () => {
    setShowMyGamesOnly(!showMyGamesOnly);
  };

  return (
    <div
      style={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}
    >
      <PageNav />
      <div style={{ flex: 1 }}>
        <div className="main_div">
          <div className="center_me">
            <div
              className="border_radius  center_me"
              style={{ padding: 5, marginBottom: 10 }}
            >
              <div
                className="background_1 border_radius"
                style={{ width: 300, position: "relative" }}
              >
                {/* Sliding indicator */}
                <div
                  className="background_3 border_radius toggle-slider"
                  style={{
                    position: "absolute",
                    width: 150,
                    height: "100%",
                    top: 0,
                    border: "2px solid #007bff",
                    boxSizing: "border-box",
                    transform: isViewingCurrentGames
                      ? "translateX(0)"
                      : "translateX(150px)",
                  }}
                />
                <div
                  className="font_family_base bill-type-button-base border_radius"
                  onClick={() =>
                    !isViewingCurrentGames && UpdateViewingCurrentGames()
                  }
                  style={{
                    textAlign: "center",
                    position: "relative",
                    zIndex: 1,
                    cursor: "pointer",
                  }}
                >
                  Current
                </div>
                <div
                  className="font_family_base bill-type-button-base border_radius"
                  onClick={() =>
                    isViewingCurrentGames && UpdateViewingCurrentGames()
                  }
                  style={{
                    textAlign: "center",
                    position: "relative",
                    zIndex: 1,
                    cursor: "pointer",
                  }}
                >
                  Upcoming
                </div>
              </div>
            </div>
          </div>
          <div className="show-desktop">
            <div className="games-filters">
              <div
                className={`games-checkbox-bubble background_1 border_radius ${
                  showAlertsOnly
                    ? "games-checkbox-active background_3"
                    : "games-checkbox-inactive background_1"
                }`}
                onClick={UpdateAlertsOnly}
              >
                <input
                  type="checkbox"
                  checked={showAlertsOnly}
                  onChange={UpdateAlertsOnly}
                  className="games-checkbox"
                />
                <span className="font_family_base games-checkbox-label">
                  Alerts Only
                </span>
              </div>
              <div
                className={`games-checkbox-bubble background_1 border_radius ${
                  showMyGamesOnly
                    ? "games-checkbox-active background_3"
                    : "games-checkbox-inactive background_1"
                }`}
                onClick={UpdateMyGamesOnly}
              >
                {/* <div
                className={`games-checkbox-bubble background_2 border_radius ${
                  showMyGamesOnly ? "games-checkbox-active" : ""
                }`}
                onClick={UpdateMyGamesOnly}
              > */}
                <input
                  type="checkbox"
                  checked={showMyGamesOnly}
                  onChange={UpdateMyGamesOnly}
                  className="games-checkbox"
                />
                <span className="font_family_base games-checkbox-label">
                  My Games Only
                </span>
              </div>
              <div
                className="games-picker-bubble games-checkbox-inactive background_3 border_radius has_border"
                onClick={() => setSportModalVisible(true)}
              >
                <CustomPicker
                  options={sports}
                  selectedValue={selectedSportName}
                  onValueChange={updateSelectedSport}
                  placeholder="Sport"
                  prefix_text="Sport: "
                  visible={sportModalVisible}
                  onClose={() => setSportModalVisible(false)}
                />
              </div>
              <div
                className="games-picker-bubble games-checkbox-inactive background_3 border_radius has_border"
                onClick={() => setBookModalVisible(true)}
              >
                <CustomPicker
                  options={books}
                  selectedValue={selectedBook}
                  onValueChange={updateSelectedBook}
                  placeholder="Sportsbook"
                  prefix_text="Book: "
                  visible={bookModalVisible}
                  onClose={() => setBookModalVisible(false)}
                />
              </div>
            </div>
          </div>
          <div className="show-mobile">
            <div className="games-filters">
              <div
                className={`games-checkbox-bubble background_1 border_radius ${
                  showAlertsOnly
                    ? "games-checkbox-active background_3"
                    : "games-checkbox-inactive background_1"
                }`}
                onClick={UpdateAlertsOnly}
              >
                <input
                  type="checkbox"
                  checked={showAlertsOnly}
                  onChange={UpdateAlertsOnly}
                  className="games-checkbox"
                />
                <span className="font_family_base games-checkbox-label">
                  Alerts Only
                </span>
              </div>
              <div
                className={`games-checkbox-bubble border_radius ${
                  showMyGamesOnly
                    ? "games-checkbox-active background_3"
                    : "games-checkbox-inactive background_1"
                }`}
                onClick={UpdateMyGamesOnly}
              >
                <input
                  type="checkbox"
                  checked={showMyGamesOnly}
                  onChange={UpdateMyGamesOnly}
                  className="games-checkbox"
                />
                <span className="font_family_base games-checkbox-label">
                  My Games Only
                </span>
              </div>
            </div>
            <div className="games-filters">
              <div
                className="games-picker-bubble background_3 border_radius has_border"
                onClick={() => setSportModalVisible(true)}
              >
                <CustomPicker
                  options={sports}
                  selectedValue={selectedSportName}
                  onValueChange={updateSelectedSport}
                  placeholder="Sport"
                  prefix_text="Sport: "
                  visible={sportModalVisible}
                  onClose={() => setSportModalVisible(false)}
                />
              </div>
              <div
                className="games-picker-bubble background_3 border_radius has_border"
                onClick={() => setBookModalVisible(true)}
              >
                <CustomPicker
                  options={books}
                  selectedValue={selectedBook}
                  onValueChange={updateSelectedBook}
                  placeholder="Sportsbook"
                  prefix_text="Book: "
                  visible={bookModalVisible}
                  onClose={() => setBookModalVisible(false)}
                />
              </div>
            </div>
          </div>
        </div>
        <div style={{ padding: "0 10px" }}>
          <div className="games-scroll-container  border_radius">
            {isActiveGamesLoading && !activeGames["events"] ? (
              <div className="games-loading">Loading...</div>
            ) : activeGamesError !== null && !activeGames["events"] ? (
              <ErrorMessage message="Sorry, we ran into an issue" />
            ) : (
              <GamesComponent
                events={
                  Array(
                    (activeGames as Record<string, unknown>)["events"] || [],
                  ) as Array<Record<string, unknown>>
                }
                rules={
                  ((activeGames as Record<string, unknown>)["triggers"] ||
                    {}) as { [key: string]: unknown }
                }
                showActive={isViewingCurrentGames}
                showMyGames={showMyGamesOnly}
                showOnlyAlerts={showAlertsOnly}
                showSport={selectedSport}
                book={selectedBook.toLocaleLowerCase()}
              />
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
